// ===========================================================
// ADVANCED BALANCE SHEET MODULE
// GAAP/IFRS Compliant Financial Reporting
// ===========================================================

let balanceSheetData = null;
let comparisonData = null;
let assetsChart = null;
let liabilitiesChart = null;

// Chart.js load karein agar available nahi hai
if (typeof Chart === 'undefined') {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js';
    document.head.appendChild(script);
}

function initBalanceSheetModule() {
    console.log('🏦 Initializing Advanced Balance Sheet Module...');
    
    // Default date set karein (Today)
    const today = new Date().toISOString().split('T')[0];
    const dateInput = document.getElementById('bs-date-as-of');
    if (dateInput && !dateInput.value) {
        dateInput.value = today;
    }
    
    // Initial load
    refreshBalanceSheet();
    
    // Auto-refresh har 5 minute mein
    setInterval(refreshBalanceSheet, 300000);
}

async function refreshBalanceSheet() {
    window.toggleLoader(true);
    
    try {
        const asOfDate = document.getElementById('bs-date-as-of').value;
        const comparisonType = document.getElementById('bs-comparison-period').value;
        
        // Current period data fetch karein
        balanceSheetData = await calculateAdvancedBalanceSheet(asOfDate);
        
        // Comparison data fetch karein agar required ho
        if (comparisonType !== 'none') {
            const compareDate = getComparisonDate(asOfDate, comparisonType);
            comparisonData = await calculateAdvancedBalanceSheet(compareDate);
        } else {
            comparisonData = null;
        }
        
        // UI Update karein
        renderAdvancedBalanceSheet();
        updateFinancialRatios();
        renderCharts();
        updateMetricsCards();
        
    } catch (error) {
        console.error('Balance Sheet Error:', error);
        alert('Error generating balance sheet. Check console for details.');
    } finally {
        window.toggleLoader(false);
    }
}

async function calculateAdvancedBalanceSheet(asOfDate) {
    const date = new Date(asOfDate);
    const endOfDay = new Date(date.setHours(23, 59, 59, 999));
    
    // Shopify data fetch karein
    const orders = MASTER_DB.orders || [];
    const products = MASTER_DB.products || [];
    const customers = MASTER_DB.customers || [];
    
    // Filter orders by date
    const relevantOrders = orders.filter(o => new Date(o.created_at) <= endOfDay);
    const paidOrders = relevantOrders.filter(o => o.financial_status === 'paid');
    
    // 1. CURRENT ASSETS CALCULATION
    
    // Cash & Cash Equivalents (Paid orders - Refunds - Expenses estimate)
    const totalRevenue = paidOrders.reduce((sum, o) => {
        const total = parseFloat(o.total_price) || 0;
        return sum + convertToStoreCurrency(total, getOrderCurrency(o));
    }, 0);
    
    const cashAndBank = totalRevenue * 0.35; // Assume 35% liquid cash
    
    // Accounts Receivable (Authorized/Pending payments)
    const receivableOrders = relevantOrders.filter(o => 
        (o.financial_status === 'authorized' || o.financial_status === 'pending') &&
        o.fulfillment_status !== 'cancelled'
    );
    const accountsReceivable = receivableOrders.reduce((sum, o) => {
        return sum + convertToStoreCurrency(parseFloat(o.total_price) || 0, getOrderCurrency(o));
    }, 0);
    
    // Inventory Valuation (FIFO Method)
    const inventoryValue = calculateInventoryValue(products);
    
    // Prepaid Expenses (Estimated)
    const prepaidExpenses = totalRevenue * 0.02;
    
    // 2. NON-CURRENT ASSETS
    
    // Fixed Assets (Equipment, Software - Estimated depreciation)
    const fixedAssets = 15000; // Base fixed assets value
    const accumulatedDepreciation = fixedAssets * 0.15; // 15% depreciation
    const netFixedAssets = fixedAssets - accumulatedDepreciation;
    
    // Long-term Receivables (Installment orders)
    const longTermReceivables = accountsReceivable * 0.1;
    
    // 3. CURRENT LIABILITIES
    
    // Accounts Payable (Estimated based on inventory)
    const accountsPayable = inventoryValue * 0.4;
    
    // Tax Payable (5% VAT assumption)
    const taxPayable = totalRevenue * 0.05;
    
    // Accrued Expenses
    const accruedExpenses = totalRevenue * 0.08;
    
    // Short-term Debt
    const shortTermDebt = 0;
    
    // 4. NON-CURRENT LIABILITIES
    
    // Long-term Debt
    const longTermDebt = 0;
    
    // Deferred Tax
    const deferredTax = 0;
    
    // 5. EQUITY
    
    // Owner's Capital
    const ownerCapital = 25000;
    
    // Retained Earnings (Accumulated profit)
    const totalCosts = (inventoryValue * 0.6) + (totalRevenue * 0.2); // COGS + Operating expenses
    const netIncome = totalRevenue - totalCosts;
    const retainedEarnings = Math.max(0, netIncome * 0.7); // 70% retained
    
    // Dividends/Distributions
    const dividends = Math.max(0, netIncome * 0.3); // 30% distributed
    
    return {
        date: asOfDate,
        assets: {
            current: {
                cashAndBank,
                accountsReceivable,
                inventory: inventoryValue,
                prepaidExpenses,
                total: cashAndBank + accountsReceivable + inventoryValue + prepaidExpenses
            },
            nonCurrent: {
                fixedAssets,
                accumulatedDepreciation: -accumulatedDepreciation,
                netFixedAssets,
                longTermReceivables,
                total: netFixedAssets + longTermReceivables
            },
            total: 0 // Calculate later
        },
        liabilities: {
            current: {
                accountsPayable,
                taxPayable,
                accruedExpenses,
                shortTermDebt,
                total: accountsPayable + taxPayable + accruedExpenses + shortTermDebt
            },
            nonCurrent: {
                longTermDebt,
                deferredTax,
                total: longTermDebt + deferredTax
            },
            total: 0 // Calculate later
        },
        equity: {
            ownerCapital,
            retainedEarnings,
            dividends: -dividends,
            currentYearEarnings: netIncome,
            total: ownerCapital + retainedEarnings - dividends + netIncome
        }
    };
}

function calculateInventoryValue(products) {
    let totalValue = 0;
    
    products.forEach(product => {
        if (!product.variants) return;
        
        product.variants.forEach(variant => {
            const qty = parseInt(variant.inventory_quantity) || 0;
            // Cost price estimate (70% of sale price agar cost price na mile)
            const price = parseFloat(variant.price) || 0;
            const costPrice = variant.compare_at_price ? 
                parseFloat(variant.compare_at_price) * 0.7 : 
                price * 0.6;
            
            totalValue += (qty * costPrice);
        });
    });
    
    return totalValue;
}

function getComparisonDate(currentDate, type) {
    const date = new Date(currentDate);
    
    switch(type) {
        case 'prev-month':
            date.setMonth(date.getMonth() - 1);
            break;
        case 'prev-quarter':
            date.setMonth(date.getMonth() - 3);
            break;
        case 'prev-year':
            date.setFullYear(date.getFullYear() - 1);
            break;
        default:
            return currentDate;
    }
    
    return date.toISOString().split('T')[0];
}

function renderAdvancedBalanceSheet() {
    if (!balanceSheetData) return;
    
    const data = balanceSheetData;
    
    // Calculate totals
    data.assets.total = data.assets.current.total + data.assets.nonCurrent.total;
    data.liabilities.total = data.liabilities.current.total + data.liabilities.nonCurrent.total;
    
    // Update Report Date
    document.getElementById('bs-report-date').innerText = `As of ${new Date(data.date).toLocaleDateString('en-GB', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    })}`;
    
    // Render Current Assets
    const currentAssetsHTML = `
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Cash & Bank</span>
            <span style="font-weight: 600;">${formatPrice(data.assets.current.cashAndBank)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Accounts Receivable</span>
            <span style="font-weight: 600;">${formatPrice(data.assets.current.accountsReceivable)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Inventory (FIFO Valuation)</span>
            <span style="font-weight: 600;">${formatPrice(data.assets.current.inventory)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Prepaid Expenses</span>
            <span style="font-weight: 600;">${formatPrice(data.assets.current.prepaidExpenses)}</span>
        </div>
    `;
    document.getElementById('bs-current-assets-list').innerHTML = currentAssetsHTML;
    document.getElementById('bs-total-current-assets').innerText = formatPrice(data.assets.current.total);
    
    // Render Non-Current Assets
    const nonCurrentAssetsHTML = `
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Fixed Assets (Cost)</span>
            <span style="font-weight: 600;">${formatPrice(data.assets.nonCurrent.fixedAssets)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6; color: #ef4444;">
            <span style="margin-left: 20px;">Less: Accumulated Depreciation</span>
            <span style="font-weight: 600;">(${formatPrice(Math.abs(data.assets.nonCurrent.accumulatedDepreciation))})</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-weight: 600;">
            <span>Net Fixed Assets</span>
            <span>${formatPrice(data.assets.nonCurrent.netFixedAssets)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Long-term Receivables</span>
            <span style="font-weight: 600;">${formatPrice(data.assets.nonCurrent.longTermReceivables)}</span>
        </div>
    `;
    document.getElementById('bs-noncurrent-assets-list').innerHTML = nonCurrentAssetsHTML;
    document.getElementById('bs-total-noncurrent-assets').innerText = formatPrice(data.assets.nonCurrent.total);
    
    // Grand Total Assets
    document.getElementById('bs-grand-total-assets').innerText = formatPrice(data.assets.total);
    
    // Render Current Liabilities
    const currentLiabHTML = `
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Accounts Payable</span>
            <span style="font-weight: 600;">${formatPrice(data.liabilities.current.accountsPayable)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Tax Payable (VAT)</span>
            <span style="font-weight: 600;">${formatPrice(data.liabilities.current.taxPayable)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Accrued Expenses</span>
            <span style="font-weight: 600;">${formatPrice(data.liabilities.current.accruedExpenses)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Short-term Debt</span>
            <span style="font-weight: 600;">${formatPrice(data.liabilities.current.shortTermDebt)}</span>
        </div>
    `;
    document.getElementById('bs-current-liabilities-list').innerHTML = currentLiabHTML;
    document.getElementById('bs-total-current-liab').innerText = formatPrice(data.liabilities.current.total);
    
    // Render Non-Current Liabilities
    const nonCurrentLiabHTML = `
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Long-term Debt</span>
            <span style="font-weight: 600;">${formatPrice(data.liabilities.nonCurrent.longTermDebt)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Deferred Tax Liability</span>
            <span style="font-weight: 600;">${formatPrice(data.liabilities.nonCurrent.deferredTax)}</span>
        </div>
    `;
    document.getElementById('bs-noncurrent-liabilities-list').innerHTML = nonCurrentLiabHTML;
    document.getElementById('bs-total-noncurrent-liab').innerText = formatPrice(data.liabilities.nonCurrent.total);
    
    // Grand Total Liabilities
    document.getElementById('bs-grand-total-liab').innerText = formatPrice(data.liabilities.total);
    
    // Render Equity
    const equityHTML = `
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Owner's Capital</span>
            <span style="font-weight: 600;">${formatPrice(data.equity.ownerCapital)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Retained Earnings (Cumulative)</span>
            <span style="font-weight: 600;">${formatPrice(data.equity.retainedEarnings)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6;">
            <span>Current Year Earnings</span>
            <span style="font-weight: 600; color: #10b981;">${formatPrice(data.equity.currentYearEarnings)}</span>
        </div>
        <div class="bs-line-item" style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6; color: #ef4444;">
            <span>Less: Dividends/Distributions</span>
            <span style="font-weight: 600;">(${formatPrice(Math.abs(data.equity.dividends))})</span>
        </div>
    `;
    document.getElementById('bs-equity-list').innerHTML = equityHTML;
    document.getElementById('bs-grand-total-equity').innerText = formatPrice(data.equity.total);
    
    // Verify Balance
    const totalLiabEquity = data.liabilities.total + data.equity.total;
    const variance = Math.abs(data.assets.total - totalLiabEquity);
    const isBalanced = variance < 0.01;
    
    document.getElementById('bs-verification-status').innerHTML = isBalanced ? 
        '<span style="color: #10b981;">✅ Balanced</span>' : 
        '<span style="color: #ef4444;">❌ Not Balanced</span>';
    
    document.getElementById('bs-variance-amount').innerText = `Variance: ${formatPrice(variance)}`;
    
    const varianceBar = document.querySelector('#bs-variance-bar div');
    if (varianceBar) {
        varianceBar.style.width = isBalanced ? '100%' : '50%';
        varianceBar.style.background = isBalanced ? '#10b981' : '#ef4444';
    }
    
    // Alert badge show/hide
    const badge = document.getElementById('bs-alert-badge');
    if (badge) {
        badge.style.display = !isBalanced ? 'inline-block' : 'none';
    }
}

function updateFinancialRatios() {
    if (!balanceSheetData) return;
    
    const data = balanceSheetData;
    const currentAssets = data.assets.current.total;
    const currentLiab = data.liabilities.current.total;
    const inventory = data.assets.current.inventory;
    const totalAssets = data.assets.total;
    const totalLiab = data.liabilities.total;
    const totalEquity = data.equity.total;
    
    // Current Ratio = Current Assets / Current Liabilities
    const currentRatio = currentLiab > 0 ? (currentAssets / currentLiab).toFixed(2) : '0.00';
    document.getElementById('ratio-current').innerText = currentRatio;
    
    const crStatus = document.getElementById('ratio-current-status');
    if (currentRatio >= 1.5) {
        crStatus.innerText = 'Excellent';
        crStatus.style.color = '#10b981';
    } else if (currentRatio >= 1.0) {
        crStatus.innerText = 'Good';
        crStatus.style.color = '#f59e0b';
    } else {
        crStatus.innerText = 'Warning';
        crStatus.style.color = '#ef4444';
    }
    
    // Quick Ratio = (Current Assets - Inventory) / Current Liabilities
    const quickRatio = currentLiab > 0 ? ((currentAssets - inventory) / currentLiab).toFixed(2) : '0.00';
    document.getElementById('ratio-quick').innerText = quickRatio;
    
    const qrStatus = document.getElementById('ratio-quick-status');
    if (quickRatio >= 1.0) {
        qrStatus.innerText = 'Healthy';
        qrStatus.style.color = '#10b981';
    } else if (quickRatio >= 0.5) {
        qrStatus.innerText = 'Fair';
        qrStatus.style.color = '#f59e0b';
    } else {
        qrStatus.innerText = 'Risk';
        qrStatus.style.color = '#ef4444';
    }
    
    // Debt-to-Equity = Total Liabilities / Total Equity
    const debtEquity = totalEquity > 0 ? (totalLiab / totalEquity).toFixed(2) : '0.00';
    document.getElementById('ratio-debt-equity').innerText = debtEquity;
    
    const deStatus = document.getElementById('ratio-de-status');
    if (debtEquity <= 0.5) {
        deStatus.innerText = 'Low Risk';
        deStatus.style.color = '#10b981';
    } else if (debtEquity <= 1.0) {
        deStatus.innerText = 'Moderate';
        deStatus.style.color = '#f59e0b';
    } else {
        deStatus.innerText = 'High Risk';
        deStatus.style.color = '#ef4444';
    }
    
    // Return on Assets = Net Income / Total Assets
    const roa = totalAssets > 0 ? ((data.equity.currentYearEarnings / totalAssets) * 100).toFixed(2) : '0.00';
    document.getElementById('ratio-roa').innerText = roa + '%';
}

function updateMetricsCards() {
    if (!balanceSheetData) return;
    
    const data = balanceSheetData;
    
    // Update values
    document.getElementById('bs-metric-assets').innerText = formatPrice(data.assets.total);
    document.getElementById('bs-metric-liabilities').innerText = formatPrice(data.liabilities.total);
    document.getElementById('bs-metric-equity').innerText = formatPrice(data.equity.total);
    
    // Working Capital = Current Assets - Current Liabilities
    const workingCapital = data.assets.current.total - data.liabilities.current.total;
    document.getElementById('bs-metric-wc').innerText = formatPrice(workingCapital);
    
    const wcRatio = data.liabilities.current.total > 0 ? 
        (workingCapital / data.liabilities.current.total).toFixed(2) : '0.00';
    document.getElementById('bs-metric-wc-ratio').innerText = `Ratio: ${wcRatio}x`;
    
    // Comparison calculations
    if (comparisonData) {
        const calcChange = (current, previous) => {
            if (previous === 0) return 'N/A';
            const change = ((current - previous) / previous) * 100;
            const symbol = change >= 0 ? '↑' : '↓';
            const color = change >= 0 ? '#10b981' : '#ef4444';
            return `<span style="color: ${color};">${symbol} ${Math.abs(change).toFixed(1)}%</span>`;
        };
        
        document.getElementById('bs-metric-assets-change').innerHTML = calcChange(data.assets.total, comparisonData.assets.total);
        document.getElementById('bs-metric-liab-change').innerHTML = calcChange(data.liabilities.total, comparisonData.liabilities.total);
        document.getElementById('bs-metric-equity-change').innerHTML = calcChange(data.equity.total, comparisonData.equity.total);
    }
}

function renderCharts() {
    if (!balanceSheetData || typeof Chart === 'undefined') return;
    
    const data = balanceSheetData;
    
    // Assets Doughnut Chart (Normal size)
    const assetsCtx = document.getElementById('assetsChart')?.getContext('2d');
    if (assetsCtx) {
        if (assetsChart) assetsChart.destroy();
        
        assetsChart = new Chart(assetsCtx, {
            type: 'doughnut',
            data: {
                labels: ['Cash', 'Receivables', 'Inventory', 'Fixed'],
                datasets: [{
                    data: [
                        data.assets.current.cashAndBank,
                        data.assets.current.accountsReceivable,
                        data.assets.current.inventory,
                        data.assets.nonCurrent.netFixedAssets
                    ],
                    backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6'],
                    borderWidth: 2,
                    borderColor: '#fff'
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { 
                        position: 'bottom',
                        labels: { 
                            boxWidth: 12, 
                            padding: 10,
                            font: { size: 11 } 
                        } 
                    }
                },
                cutout: '60%'
            }
        });
    }
    
    // Liabilities Bar Chart (Compact size)
    const liabCtx = document.getElementById('liabilitiesChart')?.getContext('2d');
    if (liabCtx) {
        if (liabilitiesChart) liabilitiesChart.destroy();
        
        liabilitiesChart = new Chart(liabCtx, {
            type: 'bar',
            data: {
                labels: ['Current', 'Long-term', 'Equity'],
                datasets: [{
                    label: 'AED',
                    data: [
                        data.liabilities.current.total,
                        data.liabilities.nonCurrent.total,
                        data.equity.total
                    ],
                    backgroundColor: ['#ef4444', '#f87171', '#3b82f6'],
                    borderRadius: 6,
                    barThickness: 35,  // Bar width choti
                    maxBarThickness: 40
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },  // Legend hata di
                    tooltip: {
                        callbacks: {
                            label: function(context) {
                                return 'AED ' + context.parsed.y.toLocaleString();
                            }
                        }
                    }
                },
                scales: {
                    y: { 
                        beginAtZero: true,
                        ticks: {
                            font: { size: 10 },
                            maxTicksLimit: 5,  // Kam ticks
                            callback: function(value) {
                                if(value >= 1000000) return (value/1000000).toFixed(1) + 'M';
                                if(value >= 1000) return (value/1000).toFixed(0) + 'k';
                                return value;
                            }
                        },
                        grid: { color: '#f3f4f6' }
                    },
                    x: {
                        ticks: { font: { size: 11 } },
                        grid: { display: false }
                    }
                }
            }
        });
    }
}

function exportBalanceSheetAdvanced(type) {
    if (!balanceSheetData) return;
    
    const data = balanceSheetData;
    const timestamp = new Date().toISOString().split('T')[0];
    
    if (type === 'excel') {
        const wb = XLSX.utils.book_new();
        
        const ws_data = [
            ['BALANCE SHEET - ' + data.date],
            ['Generated: ' + new Date().toLocaleString()],
            [''],
            ['ASSETS', '', ''],
            ['Current Assets', '', ''],
            ['Cash & Bank', data.assets.current.cashAndBank, ''],
            ['Accounts Receivable', data.assets.current.accountsReceivable, ''],
            ['Inventory', data.assets.current.inventory, ''],
            ['Prepaid Expenses', data.assets.current.prepaidExpenses, ''],
            ['Total Current Assets', data.assets.current.total, ''],
            ['', '', ''],
            ['Non-Current Assets', '', ''],
            ['Fixed Assets (Net)', data.assets.nonCurrent.netFixedAssets, ''],
            ['Long-term Receivables', data.assets.nonCurrent.longTermReceivables, ''],
            ['Total Non-Current Assets', data.assets.nonCurrent.total, ''],
            ['', '', ''],
            ['TOTAL ASSETS', data.assets.total, ''],
            ['', '', ''],
            ['LIABILITIES', '', ''],
            ['Current Liabilities', '', ''],
            ['Accounts Payable', data.liabilities.current.accountsPayable, ''],
            ['Tax Payable', data.liabilities.current.taxPayable, ''],
            ['Accrued Expenses', data.liabilities.current.accruedExpenses, ''],
            ['Total Current Liabilities', data.liabilities.current.total, ''],
            ['', '', ''],
            ['Non-Current Liabilities', '', ''],
            ['Long-term Debt', data.liabilities.nonCurrent.longTermDebt, ''],
            ['Total Non-Current Liabilities', data.liabilities.nonCurrent.total, ''],
            ['', '', ''],
            ['TOTAL LIABILITIES', data.liabilities.total, ''],
            ['', '', ''],
            ['EQUITY', '', ''],
            ["Owner's Capital", data.equity.ownerCapital, ''],
            ['Retained Earnings', data.equity.retainedEarnings, ''],
            ['Current Year Earnings', data.equity.currentYearEarnings, ''],
            ['Less: Dividends', data.equity.dividends, ''],
            ['TOTAL EQUITY', data.equity.total, ''],
            ['', '', ''],
            ['VERIFICATION', '', ''],
            ['Assets', data.assets.total, ''],
            ['Liabilities + Equity', data.liabilities.total + data.equity.total, ''],
            ['Variance', Math.abs(data.assets.total - (data.liabilities.total + data.equity.total)), '']
        ];
        
        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        XLSX.utils.book_append_sheet(wb, ws, "Balance Sheet");
        XLSX.writeFile(wb, `Balance_Sheet_Advanced_${timestamp}.xlsx`);
        
    } else if (type === 'pdf') {
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();
        
        doc.setFontSize(20);
        doc.text("Balance Sheet", 14, 20);
        doc.setFontSize(12);
        doc.text(`As of: ${data.date}`, 14, 30);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 40);
        
        let yPos = 60;
        
        // Assets Table
        doc.setFontSize(14);
        doc.text("ASSETS", 14, yPos);
        yPos += 10;
        
        doc.autoTable({
            startY: yPos,
            head: [['Description', 'Amount (AED)']],
            body: [
                ['Current Assets:', ''],
                ['Cash & Bank', formatPrice(data.assets.current.cashAndBank)],
                ['Accounts Receivable', formatPrice(data.assets.current.accountsReceivable)],
                ['Inventory', formatPrice(data.assets.current.inventory)],
                ['Prepaid Expenses', formatPrice(data.assets.current.prepaidExpenses)],
                ['Total Current Assets', formatPrice(data.assets.current.total)],
                ['', ''],
                ['Non-Current Assets:', ''],
                ['Net Fixed Assets', formatPrice(data.assets.nonCurrent.netFixedAssets)],
                ['Long-term Receivables', formatPrice(data.assets.nonCurrent.longTermReceivables)],
                ['Total Non-Current Assets', formatPrice(data.assets.nonCurrent.total)],
                ['', ''],
                ['TOTAL ASSETS', formatPrice(data.assets.total)]
            ],
            theme: 'grid',
            headStyles: { fillColor: [16, 185, 129] },
            styles: { fontSize: 10 }
        });
        
        yPos = doc.lastAutoTable.finalY + 20;
        
        // Check if new page needed
        if (yPos > 250) {
            doc.addPage();
            yPos = 20;
        }
        
        // Liabilities Table
        doc.setFontSize(14);
        doc.text("LIABILITIES", 14, yPos);
        yPos += 10;
        
        doc.autoTable({
            startY: yPos,
            head: [['Description', 'Amount (AED)']],
            body: [
                ['Current Liabilities:', ''],
                ['Accounts Payable', formatPrice(data.liabilities.current.accountsPayable)],
                ['Tax Payable', formatPrice(data.liabilities.current.taxPayable)],
                ['Accrued Expenses', formatPrice(data.liabilities.current.accruedExpenses)],
                ['Total Current Liabilities', formatPrice(data.liabilities.current.total)],
                ['', ''],
                ['Non-Current Liabilities:', ''],
                ['Long-term Debt', formatPrice(data.liabilities.nonCurrent.longTermDebt)],
                ['Total Non-Current Liabilities', formatPrice(data.liabilities.nonCurrent.total)],
                ['', ''],
                ['TOTAL LIABILITIES', formatPrice(data.liabilities.total)]
            ],
            theme: 'grid',
            headStyles: { fillColor: [239, 68, 68] },
            styles: { fontSize: 10 }
        });
        
        doc.save(`Balance_Sheet_Advanced_${timestamp}.pdf`);
    }
}

function printBalanceSheet() {
    window.print();
}

// Utility: Get Order Currency (from erp-core.js)
function getOrderCurrency(order) {
    return order.currency || 
           order.presentment_currency || 
           order.total_price_set?.presentment_money?.currency_code ||
           'AED';
}